import React, { useCallback } from "react";
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  SelectChangeEvent,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { GroupOption, usePortalGroups } from "../hooks/usePortalGroups";

type Props = {
  value?: string;
  onChange?: (val: string) => void;
  groupsData?: {
    groups: GroupOption[];
    loading: boolean;
    error: string | null;
  };
};

const GroupSelector: React.FC<Props> = ({ value, onChange, groupsData }) => {
  const { portalUid } = useParams<{ portalUid: string }>();
  const portalGroupsState = usePortalGroups(portalUid, !groupsData);
  const { groups, loading, error } = groupsData || portalGroupsState;

  const handleChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const newValue = e.target.value;
      onChange?.(newValue);
    },
    [onChange]
  );

  return (
    <FormControl fullWidth error={!!error}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          mb: 1.5,
          lineHeight: "22px",
        }}
      >
        Group/Subgroup
      </Typography>
      <Select
        labelId="group-select-label"
        id="group-select-label"
        value={value || ""}
        onChange={handleChange}
        disabled={loading || groups.length === 0}
        displayEmpty
        renderValue={(selected) => {
          if (!selected)
            return (
              <span
                style={{ color: "#667085", fontSize: "16px", fontWeight: 500 }}
              >
                Select one
              </span>
            );

          const [groupId, subGroupId] = selected.split(":");
          const group = groups.find((g) => g.id === groupId);

          if (!group)
            return (
              <span
                style={{ color: "#667085", fontSize: "16px", fontWeight: 500 }}
              >
                Select one
              </span>
            );

          if (subGroupId) {
            const subGroup = group.subGroups.find((sg) => sg.id === subGroupId);
            return subGroup ? `${group.name} > ${subGroup.name}` : group.name;
          }

          return group.name;
        }}
        sx={{
          height: "44px",
          borderRadius: "12px",
          "& .MuiSelect-select": {
            fontSize: 16,
            color: value ? "#101828" : "#667085",
            padding: "10px 14px",
            fontWeight: value ? 500 : 400,
          },
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              width: 250,
              borderRadius: "8px",
              marginTop: "4px",
            },
          },
          MenuListProps: {
            component: "div",
          },
        }}
        startAdornment={
          loading ? (
            <Box sx={{ display: "flex", py: 1 }}>
              <CircularProgress size={24} />
            </Box>
          ) : null
        }
      >
        <MenuItem value="">
          <em>Clear selection</em>
        </MenuItem>

        {error ? (
          <Typography color="error" variant="body2" sx={{ p: 2 }}>
            {error}
          </Typography>
        ) : loading ? (
          <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : groups.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2, color: "text.secondary" }}>
            No groups available
          </Typography>
        ) : (
          groups.flatMap((group) => [
            <MenuItem
              key={`group-${group.id}`}
              value={group.id}
              sx={{
                pl: 2,
                fontWeight: 600,
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              {group.name}
            </MenuItem>,

            ...group.subGroups.map((subGroup) => (
              <MenuItem
                key={`subgroup-${subGroup.id}`}
                value={`${group.id}:${subGroup.id}`}
                sx={{
                  pl: 4,
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                {subGroup.name}
              </MenuItem>
            )),
          ])
        )}
      </Select>
    </FormControl>
  );
};

export default GroupSelector;
